import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter, type Event } from '@patsnap/tahiti-core/common/event';
import { AISearchModel } from '../model/ai-search.model';
import { AISearchRepository, type AISearchRepositoryElementsData } from './ai-search.repository.interface';
import { TechStep } from '../type/ai-search.enum';
import type { AISearchState, DescriptionState, ElementsState, ElementItem, InitElementsParams, RelatedIpcItem } from '../type/ai-search.type';
import _ from 'lodash';
import { AISearchDescriptionService } from './ai-search-description.service';
import { AISearchSummaryService } from './ai-search-summary.service';
import { AISearchElementsService } from './ai-search-elements.service';
import { AISearchStrategyService } from './ai-search-strategy.service';
import { HistoryService } from '@/services/history/service/history.service';

export const AISearchService = Symbol('AISearchService');

export interface AISearchService {
    getModelData: () => AISearchState;
    readonly onStatesChange: Event<AISearchState>;

    restart: () => void;
    handleChangeStep: (type: 'next' | 'prev') => void;
    handleGetSummary: (desc: DescriptionState['desc']) => Promise<void>;
    handleGetElements: (param: InitElementsParams) => Promise<void>;
    handleGetQueryList: (param: ElementsState) => Promise<void>;
}

@injectable()
export class AISearchServiceImpl implements AISearchService {
    private _onStatesChangeEmitter = new Emitter<AISearchState>();
    readonly onStatesChange = this._onStatesChangeEmitter.event;
    private _stepList = [
        TechStep.TECH_DESC,
        TechStep.TECH_SUMMARY,
        TechStep.TECH_ELEMENTS,
        TechStep.TECH_STRATEGY,
    ];

    constructor(
        @inject(AISearchModel)
        private _model: AISearchModel,
        @inject(AISearchRepository)
        private _repository: AISearchRepository,
        @inject(AISearchDescriptionService)
        private _descriptionService: AISearchDescriptionService,
        @inject(AISearchSummaryService)
        private _summaryService: AISearchSummaryService,
        @inject(AISearchElementsService)
        private _elementsService: AISearchElementsService,
        @inject(AISearchStrategyService)
        private _strategyService: AISearchStrategyService,
        @inject(HistoryService)
        private _historyService: HistoryService,
    ) {
        this._model.onStatesChange(this._emitModelDataChange.bind(this));
        this._descriptionService.onSummaryGet(this.handleGetSummary.bind(this));
        this._summaryService.onElementsGet(this.handleGetElements.bind(this));
        this._elementsService.onQueryListGet(this.handleGetQueryList.bind(this));
    }

    private _emitModelDataChange() {
        this._onStatesChangeEmitter.fire(this.getModelData());
    }

    private _setModelData(value: Partial<AISearchState>) {
        this._model.update(modelData => {
            return { ...modelData, ...value };
        });
    }

    public getModelData(): AISearchState {
        return this._model.modelData;
    }

    public restart() {
        this._descriptionService.handleClearDescription();
        this._setModelData({
            step: this._stepList[0],
        });
    }

    public handleChangeStep(type: 'next' | 'prev') {
        const curIndex = this._stepList.indexOf(
            this.getModelData().step,
        );
        const newIndex = type === 'next' ? curIndex + 1 : curIndex - 1;
        const newStep = this._stepList[newIndex] || this._stepList[0];

        this._setModelData({ step: newStep });
    }

    public async handleGetSummary(desc: DescriptionState['desc']) {
        this._summaryService.initSumaryState(() => {
            const taskId = this._historyService.getModelData().taskId;
            return this._repository.getSummary(taskId, desc);
        });
        this.handleChangeStep('next');
    }

    private _formatElements(res: AISearchRepositoryElementsData, param: InitElementsParams): Omit<ElementsState, 'isLoading' | 'summary'> {
        const summaryIpc: RelatedIpcItem[] = [];
        param.summary.forEach(item => summaryIpc.push(...item.relatedIpc));
        const summaryIpcFiltered = _.uniqBy(summaryIpc, 'code');

        return {
            topic: {
                ...res.topic,
                relatedIpc: param.relatedIpc.map(item => {
                    return {
                        id: item.id,
                        code: item.code,
                    };
                }),
            },
            element: {
                ...res.element,
                relatedIpc: summaryIpcFiltered.map(item => {
                    return {
                        id: item.id,
                        code: item.code,
                    };
                }),
            },
        };
    }

    public async handleGetElements(param: InitElementsParams) {
        const taskId = this._historyService.getModelData().taskId;
        this._elementsService.initElementsState(
            {
                summary: param.summary,
            },
            async () => {
                const res = await this._repository.getElements(taskId, {
                    area: param.topic,
                    topics: param.summary,
                });
                return this._formatElements(res, param);
            },
        );
        this.handleChangeStep('next');
    }

    private _formatSemantic(res: { [key: string]: string }, summaryStr: string[]) {
        const semanticList = summaryStr.map(item => ({
            id: res[item],
            value: item,
        }));
        return semanticList.filter(item => item.id);
    }

    public async handleGetQueryList(param: ElementsState) {
        const summary = this._summaryService.getModelData().summarySelected;
        const summaryStr = summary.map(item => item.desc);

        // 初始化数据
        this._strategyService.init(async () => {
            const res = await this._repository.getSemantic(summaryStr);
            return {
                ...param,
                element: {
                    ...param.element,
                    elements: param.element.elements.filter((item: ElementItem) => item.isChecked),
                },
                semantic: this._formatSemantic(res, summaryStr),
            };
        });

        this.handleChangeStep('next');
    }
}
